import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  TrendingUp, 
  DollarSign, 
  Coins,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminCurrenciesManagementPage = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [formData, setFormData] = useState({
    currency_code: '',
    name: '',
    icon: '',
    category: 'fiat',
    pricing_source: 'manual',
    is_tradable: true,
    spread_percentage: 0.5,
    routing_priority: 5
  });

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/exchange/currencies`, {
        credentials: 'include'
      });
      const data = await response.json();
      setCurrencies(data.currencies || []);
    } catch (error) {
      toast.error('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCurrency) {
        // Update existing currency
        const response = await fetch(
          `${API_URL}/api/admin/exchange/currency/${editingCurrency.currency_code}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(formData)
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to update currency');
        }

        toast.success('Currency updated successfully');
      } else {
        // Add new currency
        const response = await fetch(`${API_URL}/api/admin/exchange/currency`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to add currency');
        }

        toast.success('Currency added successfully');
      }

      setShowAddModal(false);
      setEditingCurrency(null);
      resetForm();
      fetchCurrencies();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (currency) => {
    setEditingCurrency(currency);
    setFormData({
      currency_code: currency.currency_code,
      name: currency.name,
      icon: currency.icon || '',
      category: currency.category,
      pricing_source: currency.pricing_source,
      is_tradable: currency.is_tradable,
      spread_percentage: currency.spread_percentage || 0.5,
      routing_priority: currency.routing_priority || 5
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      currency_code: '',
      name: '',
      icon: '',
      category: 'fiat',
      pricing_source: 'manual',
      is_tradable: true,
      spread_percentage: 0.5,
      routing_priority: 5
    });
  };

  const getCategoryBadge = (category) => {
    const colors = {
      fiat: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      crypto: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      stable: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    };
    return colors[category] || colors.fiat;
  };

  const getPricingBadge = (source) => {
    const colors = {
      manual: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
      treasury: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      live_market: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    };
    return colors[source] || colors.manual;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Currency Registry</h1>
            <p className="text-muted-foreground">
              Manage tradable currencies and exchange configurations
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setEditingCurrency(null);
              setShowAddModal(true);
            }}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Currency
          </Button>
        </div>

        {/* Currency List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" />
              All Currencies ({currencies.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : currencies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No currencies found. Add your first currency to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {currencies.map((currency) => (
                  <div
                    key={currency.currency_code}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">{currency.icon || '💱'}</div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">{currency.currency_code}</span>
                          <Badge className={getCategoryBadge(currency.category)}>
                            {currency.category}
                          </Badge>
                          <Badge className={getPricingBadge(currency.pricing_source)}>
                            {currency.pricing_source.replace('_', ' ')}
                          </Badge>
                          {currency.status?.trading_enabled ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <Check className="h-3 w-3 mr-1" />
                              Tradable
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <X className="h-3 w-3 mr-1" />
                              Disabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{currency.name}</p>
                        <div className="text-xs text-muted-foreground mt-1 flex gap-3">
                          <span>Spread: {currency.spread_percentage || 0.5}%</span>
                          <span>Priority: {currency.routing_priority || 5}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(currency)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>
                  {editingCurrency ? 'Edit Currency' : 'Add New Currency'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Currency Code *</Label>
                      <Input
                        value={formData.currency_code}
                        onChange={(e) =>
                          setFormData({ ...formData, currency_code: e.target.value.toUpperCase() })
                        }
                        placeholder="BTC, USD, ZAR"
                        disabled={!!editingCurrency}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Bitcoin, US Dollar"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Icon (Emoji)</Label>
                      <Input
                        value={formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        placeholder="₿, $, R"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full border rounded-md p-2"
                        required
                      >
                        <option value="fiat">Fiat</option>
                        <option value="crypto">Crypto</option>
                        <option value="stable">Stablecoin</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Pricing Source *</Label>
                    <select
                      value={formData.pricing_source}
                      onChange={(e) => setFormData({ ...formData, pricing_source: e.target.value })}
                      className="w-full border rounded-md p-2"
                      required
                    >
                      <option value="manual">Manual (Admin Set)</option>
                      <option value="treasury">Treasury Rates</option>
                      <option value="live_market">Live Market (CoinGecko)</option>
                    </select>
                    <p className="text-xs text-muted-foreground">
                      {formData.pricing_source === 'manual' && 'Admin manually sets exchange rates'}
                      {formData.pricing_source === 'treasury' && 'Uses treasury buy/sell rates'}
                      {formData.pricing_source === 'live_market' && 'Fetches real-time market prices'}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Spread Percentage</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.spread_percentage}
                        onChange={(e) =>
                          setFormData({ ...formData, spread_percentage: parseFloat(e.target.value) })
                        }
                        placeholder="0.5"
                      />
                      <p className="text-xs text-muted-foreground">Platform markup on exchanges</p>
                    </div>

                    <div className="space-y-2">
                      <Label>Routing Priority</Label>
                      <Input
                        type="number"
                        value={formData.routing_priority}
                        onChange={(e) =>
                          setFormData({ ...formData, routing_priority: parseInt(e.target.value) })
                        }
                        placeholder="5"
                      />
                      <p className="text-xs text-muted-foreground">
                        Higher = preferred in routes (1-10)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_tradable"
                      checked={formData.is_tradable}
                      onChange={(e) =>
                        setFormData({ ...formData, is_tradable: e.target.checked })
                      }
                      className="w-4 h-4"
                    />
                    <Label htmlFor="is_tradable" className="cursor-pointer">
                      Enable for trading
                    </Label>
                  </div>

                  <div className="flex items-center gap-3 pt-4 border-t">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">
                      Changes take effect immediately. Users will see updated currencies in the
                      exchange interface.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button type="submit" className="flex-1">
                      {editingCurrency ? 'Update Currency' : 'Add Currency'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAddModal(false);
                        setEditingCurrency(null);
                        resetForm();
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
